import dayjs from "dayjs";
import { Link } from "react-router-dom";

import { CalendarIcon, DollarSign, MapPinIcon } from "@/app/assets/icons/interface/interface";
import SaveButton from "@/app/components/SaveButton";
import { Button } from "@/app/components/ui/button";
import { cn } from "@/app/handlers/cn.utils";
import { getJobDeadline, isClosedJob } from "@/app/handlers/jobStatus";
import { slugifyURL } from "@/app/handlers/urlFunctions";
import { formatJobSalary } from "@/app/handlers/utilityFunctions";
import { useSaveJob } from "@/app/hooks/useSaveJob";
import { prefetchCompany, prefetchCompanyJobs, prefetchJob } from "@/app/services/create-resume/queries";

import { SimilarJob } from "../hooks/useSimilarJobs";

interface SimilarRoleCardProps {
	job: SimilarJob;
	/** Reported when the title is followed. The link navigates itself, so this only records it. */
	onSelect: () => void;
	/** Records and opens the role. The CTA is a button, so it cannot navigate on its own. */
	onOpen: () => void;
}

/**
 * One similar role, as a card.
 *
 * Carries exactly what a job listing row carries — title, employer, category, location, deadline
 * and pay — laid out as a card so the standalone job page can stand these beside each other the
 * way it already does for similar companies. The drawer is too narrow for a grid and lists rows.
 */
export function SimilarRoleCard({ job, onSelect, onOpen }: SimilarRoleCardProps) {
	const companyId = job.company?._id;
	const companyName = job.company?.company_name || job.companyName || "";
	const logo = job.logo || job.company?.logo_url;

	const expired = isClosedJob(job);
	const deadline = getJobDeadline(job);
	const salary = formatJobSalary(job.salary);
	const hasChips = Boolean(job.category || job.isPromoted || expired);

	const { isSaved, saving, saveJob } = useSaveJob(job._id);

	// Only linkable once both halves of the slug are known; the row does the same check.
	const jobHref =
		job._id && job.jobTitle && companyName
			? `/jobs/${slugifyURL({ _id: job._id, companyName, jobTitle: job.jobTitle })}`
			: null;

	const followTitle = () => {
		onSelect();
		window.scrollTo(0, 0);
	};

	return (
		<div
			// Same surface treatment as the similar companies card this sits alongside.
			className="bg-white rounded-lg p-6 flex flex-col h-full"
			style={{ boxShadow: "0 0 0 1px rgba(0,0,0,.05), 0 2px 2px rgba(0,0,0,.04)" }}
			onMouseEnter={() => prefetchJob(job._id)}
		>
			<div className="flex items-start mb-3">
				<Link
					to={companyId ? `/companies/${companyId}` : "#"}
					state={job.company}
					className="shrink-0"
					onMouseEnter={() => {
						prefetchCompany(companyId);
						prefetchCompanyJobs(companyId);
					}}
				>
					<div className="w-12 h-12 rounded-lg overflow-hidden flex items-center justify-center border border-gray-100">
						<img
							src={logo || "/images/co-placeholder.png"}
							alt={`${companyName} logo`}
							width={48}
							height={48}
							loading="lazy"
							className="w-full h-full object-contain"
							onError={(e) => {
								(e.target as HTMLImageElement).src = "/images/co-placeholder.png";
							}}
						/>
					</div>
				</Link>

				{/* min-w-0 so a long employer name truncates instead of pushing the card wider. */}
				<div className="ml-4 min-w-0">
					<Link to={jobHref || "#"} onClick={followTitle}>
						<h3 className="text-base font-semibold text-gray-900 font-inter line-clamp-2 transition-colors duration-300 hover:text-primary-700">
							{job.jobTitle}
						</h3>
					</Link>
					<p className="text-sm text-gray-700 truncate">{companyName}</p>
				</div>
			</div>

			{/* Search results do not always carry a category, so the row only takes space once it
			    has a chip to hold — an empty one leaves a gap under the employer name. */}
			<div className={cn("flex flex-wrap gap-2", hasChips && "mb-3")}>
				{job.category && (
					<span className="inline-flex items-center h-6 px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
						{job.category}
					</span>
				)}
				{job.isPromoted && (
					<span className="inline-flex items-center h-6 px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
						Promoted
					</span>
				)}
				{expired && (
					<span className="inline-flex items-center h-6 px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
						Expired
					</span>
				)}
			</div>

			<div className="space-y-1.5 text-sm text-gray-600 font-inter">
				{(job.jobLocation?.city || job.jobLocation?.country) && (
					<p className="flex items-center">
						<MapPinIcon className="w-4 h-4 mr-2 shrink-0" />
						<span className="truncate">
							{[job.jobLocation?.city, job.jobLocation?.country].filter(Boolean).join(", ")}
						</span>
					</p>
				)}
				{deadline && (
					<p className="flex items-center">
						<CalendarIcon className="w-4 h-4 mr-2 shrink-0" />
						{dayjs(deadline).format("MMM DD, YYYY")}
					</p>
				)}
				{salary && (
					<p className="flex items-center">
						<DollarSign className="w-4 h-4 mr-2 shrink-0" />
						<span className="truncate">{salary}</span>
					</p>
				)}
			</div>

			{/* mt-auto keeps the actions on the baseline of the row, however tall each card's body runs. */}
			<div className="mt-auto pt-4 flex gap-2">
				<Button size="sm" className="flex-1" onClick={onOpen}>
					{expired ? "View" : "Explore"}
				</Button>
				{/* SaveButton sizes itself to its parent below md, where it would otherwise go full width. */}
				<div className="w-[70px] shrink-0">
					<SaveButton disabled={saving} isSaved={isSaved} onClick={saveJob} />
				</div>
			</div>
		</div>
	);
}
